<template>
  <div class="pd-wrapper">

    <!-- Header -->
    <v-card class="header-card" elevation="0">
      <div class="header-inner">
        <i class="dx-icon dx-icon-group header-icon" />
        <div>
          <div class="header-title">Dashboard Personal</div>
          <div class="header-subtitle">KPI-uri angajați · distribuții · evoluție angajări</div>
        </div>
        <v-btn class="refresh-btn ml-auto" density="compact" :loading="loading" @click="incarcaDashboard">
          <v-icon size="16" class="mr-1">mdi-refresh</v-icon> Actualizează
        </v-btn>
      </div>
    </v-card>

    <!-- Loading -->
    <div v-if="loading" class="d-flex justify-center align-center py-8">
      <v-progress-circular indeterminate color="#ff5722" size="48" />
    </div>

    <div v-else>
      <!-- Summary cards — rand 1 -->
      <v-row class="mb-3" dense>
        <v-col v-for="(card, idx) in summaryCards1" :key="card.key" cols="12" sm="6" md="4" lg="2">
          <v-card class="summary-card kpi-anim" :class="card.alertClass" elevation="0"
            :style="{ '--accent': card.color, 'animation-delay': (idx * 70) + 'ms' }">
            <div class="summary-accent" :style="{ background: card.color }" />
            <div class="summary-inner">
              <div class="summary-icon" :style="{ background: card.color + '1a' }">
                <v-icon :color="card.color" size="22">{{ card.icon }}</v-icon>
              </div>
              <div>
                <div class="summary-value" :style="{ color: card.alert ? '#F44336' : '#1a202c' }">
                  {{ card.decimals ? formatDec(animated[card.key]) : Math.round(animated[card.key] || 0) }}
                </div>
                <div class="summary-label">{{ card.label }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Summary cards — rand 2 -->
      <v-row class="mb-4" dense>
        <v-col v-for="(card, idx) in summaryCards2" :key="card.key" cols="12" sm="6" md="4" lg="2">
          <v-card class="summary-card kpi-anim" :class="card.alertClass" elevation="0"
            :style="{ '--accent': card.color, 'animation-delay': ((summaryCards1.length + idx) * 70) + 'ms' }">
            <div class="summary-accent" :style="{ background: card.color }" />
            <div class="summary-inner">
              <div class="summary-icon" :style="{ background: card.color + '1a' }">
                <v-icon :color="card.color" size="22">{{ card.icon }}</v-icon>
              </div>
              <div>
                <div class="summary-value" :style="{ color: card.alert ? '#F44336' : '#1a202c' }">
                  {{ card.decimals ? formatDec(animated[card.key]) : Math.round(animated[card.key] || 0) }}
                </div>
                <div class="summary-label">{{ card.label }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Grafice — grid 3 pe rand -->
      <v-row dense>
        <v-col v-for="(chart, idx) in visibleCharts" :key="chart.key" cols="12" md="6" :lg="chart.wide ? 12 : 4">
          <v-card class="chart-card kpi-anim" elevation="0" :style="{ 'animation-delay': (idx * 60) + 'ms' }"
            :class="{ 'chart-card--wide': chart.wide }">
            <div class="chart-header">
              <span class="chart-header-icon" :style="{ background: chart.color + '1a' }">
                <v-icon size="15" :color="chart.color">{{ chart.icon }}</v-icon>
              </span>
              {{ chart.title }}
            </div>

            <DxPieChart v-if="chart.type === 'pie' && chart.data.length" type="doughnut" :data-source="chart.data"
              :palette="chart.palette" class="chart-body">
              <DxPieSeries argument-field="Eticheta" value-field="Numar">
                <DxPieLabel :visible="true" format="fixedPoint" />
              </DxPieSeries>
              <DxPieLegend vertical-alignment="bottom" horizontal-alignment="center" />
              <DxPieTooltip :enabled="true" />
              <DxPieAnimation :enabled="true" :duration="1000" easing="easeOutCubic" />
            </DxPieChart>

            <DxChart v-else-if="chart.type === 'bar' && chart.data.length" :data-source="chart.data"
              class="chart-body" :palette="chart.palette">
              <DxCommonSeriesSettings :argument-field="chart.argField || 'Eticheta'" type="bar" :bar-padding="0.3" />
              <DxSeries value-field="Numar" :name="chart.seriesName || 'Angajați'" :color="chart.palette[0]" />
              <DxArgumentAxis>
                <DxLabel :word-wrap="'none'" :overlapping-behavior="chart.rotate ? 'rotate' : 'none'" />
              </DxArgumentAxis>
              <DxLegend :visible="false" />
              <DxTooltip :enabled="true" />
              <DxAnimation :enabled="true" :duration="1000" easing="easeOutCubic" />
            </DxChart>

            <DxChart v-else-if="chart.type === 'line' && chart.data.length" :data-source="chart.data"
              class="chart-body chart-body--wide" :palette="chart.palette">
              <DxCommonSeriesSettings :argument-field="chart.argField || 'Luna'" type="line" />
              <DxSeries value-field="Numar" :name="chart.seriesName || 'Angajări'" :color="chart.palette[0]" />
              <DxArgumentAxis>
                <DxLabel :word-wrap="'none'" :overlapping-behavior="'rotate'" />
              </DxArgumentAxis>
              <DxLegend :visible="false" />
              <DxTooltip :enabled="true" />
              <DxAnimation :enabled="true" :duration="1000" easing="easeOutCubic" />
            </DxChart>

            <EmptyChart v-else />
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, h } from 'vue'
import {
  DxChart, DxSeries, DxCommonSeriesSettings,
  DxArgumentAxis, DxLabel, DxLegend, DxTooltip, DxAnimation,
} from 'devextreme-vue/chart'
import {
  DxPieChart, DxSeries as DxPieSeries, DxLabel as DxPieLabel,
  DxLegend as DxPieLegend, DxTooltip as DxPieTooltip, DxAnimation as DxPieAnimation,
} from 'devextreme-vue/pie-chart'
import RU_PersonalDashboardService from '@/services/RU/RU_PersonalDashboard'

const EmptyChart = defineComponent({
  render() {
    return h('div', { class: 'empty-chart' }, [
      h('i', { class: 'dx-icon dx-icon-chart', style: 'font-size:36px;color:#e2e8f0' }),
      h('div', { style: 'color:#94a3b8;font-size:12px;margin-top:8px' }, 'Nu există date pentru acest grafic.'),
    ])
  },
})

interface SummaryCard {
  key: string
  label: string
  icon: string
  color: string
  decimals?: boolean
  alert?: boolean
  alertClass?: string
}

export default defineComponent({
  name: 'PersonalDashboard',

  components: {
    DxChart, DxSeries, DxCommonSeriesSettings, DxArgumentAxis, DxLabel, DxLegend, DxTooltip, DxAnimation,
    DxPieChart, DxPieSeries, DxPieLabel, DxPieLegend, DxPieTooltip, DxPieAnimation,
    EmptyChart,
  },

  data() {
    return {
      loading: false,
      dashboard: null as any,
      animated: {
        TotalActivi: 0, VarstaMedie: 0, VechimeMedie: 0, ContracteActive: 0,
        TotalPensionari: 0, ContracteExpira90Zile: 0, DocumenteExpira90Zile: 0, MedieDocumentePerAngajat: 0,
      } as Record<string, number>,
      rafHandles: [] as number[],
    }
  },

  computed: {
    summaryCards1(): SummaryCard[] {
      return [
        { key: 'TotalActivi', label: 'Angajați activi', icon: 'mdi-account-group', color: '#ff5722' },
        { key: 'VarstaMedie', label: 'Vârstă medie', icon: 'mdi-cake-variant', color: '#2196F3', decimals: true },
        { key: 'VechimeMedie', label: 'Vechime medie (ani)', icon: 'mdi-briefcase-clock', color: '#4CAF50', decimals: true },
        { key: 'ContracteActive', label: 'Contracte active', icon: 'mdi-file-sign', color: '#9C27B0' },
        { key: 'TotalPensionari', label: 'Pensionari', icon: 'mdi-seat-recline-normal', color: '#607D8B' },
        {
          key: 'ContracteExpira90Zile', label: 'Contracte expiră ≤ 90 zile', icon: 'mdi-calendar-alert', color: '#F44336',
          alert: (this.dashboard?.ContracteExpira90Zile ?? 0) > 0, alertClass: (this.dashboard?.ContracteExpira90Zile ?? 0) > 0 ? 'summary-card--alert' : '',
        },
      ]
    },
    summaryCards2(): SummaryCard[] {
      return [
        {
          key: 'DocumenteExpira90Zile', label: 'Documente expiră ≤ 90 zile', icon: 'mdi-file-clock-outline', color: '#EF4444',
          alert: (this.dashboard?.DocumenteExpira90Zile ?? 0) > 0, alertClass: (this.dashboard?.DocumenteExpira90Zile ?? 0) > 0 ? 'summary-card--alert' : '',
        },
        { key: 'MedieDocumentePerAngajat', label: 'Medie documente / angajat', icon: 'mdi-file-multiple-outline', color: '#0EA5E9', decimals: true },
      ]
    },

    charts(): any[] {
      const d = this.dashboard
      return [
        { key: 'sex', type: 'pie', title: 'Angajați pe sex', icon: 'mdi-gender-male-female', color: '#2196F3', palette: ['#2196F3', '#e91e63', '#94a3b8'], data: d?.PeSex ?? [] },
        { key: 'norma', type: 'pie', title: 'Contracte pe tip normă', icon: 'mdi-clock-outline', color: '#ff5722', palette: ['#ff5722', '#ff9800', '#4CAF50', '#94a3b8'], data: d?.PeTipNorma ?? [] },
        { key: 'durata', type: 'pie', title: 'Contracte pe tip durată', icon: 'mdi-calendar-range', color: '#059669', palette: ['#059669', '#F59E0B', '#94a3b8'], data: d?.PeTipDurata ?? [] },
        { key: 'functieBaza', type: 'pie', title: 'Angajați pe funcție de bază', icon: 'mdi-star-check-outline', color: '#8B5CF6', palette: ['#8B5CF6', '#CBD5E1'], data: d?.PeFunctieDeBaza ?? [] },
        { key: 'departament', type: 'bar', title: 'Angajați pe departament', icon: 'mdi-sitemap', color: '#2563eb', palette: ['#2563eb'], data: d?.PeDepartament ?? [], rotate: true },
        { key: 'nivel1', type: 'bar', title: 'Angajați pe punct de lucru', icon: 'mdi-office-building-marker', color: '#7c3aed', palette: ['#7c3aed'], data: d?.PeStructuraNivel1 ?? [], rotate: true },
        { key: 'varsta', type: 'bar', title: 'Angajați pe grupă de vârstă', icon: 'mdi-account-multiple', color: '#059669', palette: ['#059669'], data: d?.PeGrupaVarsta ?? [] },
        { key: 'vechime', type: 'bar', title: 'Angajați pe grupă de vechime', icon: 'mdi-briefcase-clock-outline', color: '#0891b2', palette: ['#0891b2'], data: d?.PeVechimeGrupa ?? [] },
        { key: 'studii', type: 'bar', title: 'Angajați pe nivel studii', icon: 'mdi-school-outline', color: '#d97706', palette: ['#d97706'], data: d?.PeNivelStudii ?? [], rotate: true },
        { key: 'anAngajare', type: 'bar', title: 'Angajați pe an de angajare', icon: 'mdi-calendar-plus', color: '#DB2777', palette: ['#DB2777'], data: d?.PeAnAngajare ?? [] },
        { key: 'nationalitate', type: 'bar', title: 'Angajați pe naționalitate', icon: 'mdi-earth', color: '#0D9488', palette: ['#0D9488'], data: d?.PeNationalitate ?? [], rotate: true },
        { key: 'documente', type: 'bar', title: 'Documente pe tip', icon: 'mdi-file-document-multiple', color: '#ff5722', palette: ['#ff5722'], data: d?.DocumentePeTip ?? [], rotate: true },
        { key: 'trend', type: 'line', title: 'Trend angajări (ultimele 12 luni)', icon: 'mdi-chart-line', color: '#ff5722', palette: ['#ff5722'], data: d?.AngajariPeLuna ?? [], argField: 'Luna', wide: true },
      ]
    },

    // Graficul de nationalitate se afiseaza doar daca backend-ul a avut date (altfel se sare, ca cerut)
    visibleCharts(): any[] {
      return this.charts.filter(c => c.key !== 'nationalitate' || c.data.length > 0)
    },
  },

  mounted() {
    this.incarcaDashboard()
  },

  beforeUnmount() {
    this.rafHandles.forEach(h => cancelAnimationFrame(h))
  },

  methods: {
    async incarcaDashboard() {
      this.loading = true
      try {
        const res = await RU_PersonalDashboardService.getDashboard()
        this.dashboard = res.data
        this.$nextTick(() => this.animateSummary())
      } catch (e) {
        console.error('Error loading personal dashboard:', e)
        this.dashboard = null
      } finally {
        this.loading = false
      }
    },

    animateSummary() {
      const keys = ['TotalActivi', 'VarstaMedie', 'VechimeMedie', 'ContracteActive',
        'TotalPensionari', 'ContracteExpira90Zile', 'DocumenteExpira90Zile', 'MedieDocumentePerAngajat']
      keys.forEach(key => {
        const target = Number(this.dashboard?.[key] ?? 0)
        this.tweenTo(key, target)
      })
    },

    /** Count-up animat de la 0 la target, ~900ms, ease-out cubic, prin requestAnimationFrame. */
    tweenTo(key: string, target: number) {
      const duration = 900
      const start = performance.now()
      const from = 0
      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

      const step = (now: number) => {
        const elapsed = now - start
        const t = Math.min(1, elapsed / duration)
        const eased = easeOutCubic(t)
        this.animated[key] = from + (target - from) * eased
        if (t < 1) {
          const handle = requestAnimationFrame(step)
          this.rafHandles.push(handle)
        } else {
          this.animated[key] = target
        }
      }
      const handle = requestAnimationFrame(step)
      this.rafHandles.push(handle)
    },

    formatDec(val: number | null | undefined): string {
      if (val === null || val === undefined) return '—'
      return new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(val)
    },
  },
})
</script>

<style scoped>
.pd-wrapper {
  padding: 1.5rem;
  background: #f8f9fc;
  min-height: 100%;
}

.header-card {
  border-radius: 14px !important;
  margin-bottom: 1.5rem;
  overflow: hidden;
  background: linear-gradient(90deg, #ff2c21, #ff6a4a) !important;
}

.header-inner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.header-icon {
  font-size: 26px;
  color: white;
}

.header-title {
  font-size: 16px;
  font-weight: 700;
  color: white;
}

.header-subtitle {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  margin-top: 2px;
}

.refresh-btn {
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
  height: 34px !important;
  background: rgba(255, 255, 255, 0.15) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.refresh-btn:hover {
  background: rgba(255, 255, 255, 0.25) !important;
}

/* Entrance animation, staggered via inline animation-delay */
.kpi-anim {
  animation: kpiFadeIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes kpiFadeIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .kpi-anim {
    animation: none;
  }
}

/* Summary cards */
.summary-card {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s ease;
}

.summary-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.summary-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}

.summary-card--alert {
  border-color: rgba(244, 67, 54, 0.25) !important;
}

.summary-inner {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem 1.1rem 0.95rem;
}

.summary-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-value {
  font-size: 1.85rem;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.summary-label {
  font-size: 0.7rem;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Chart cards */
.chart-card {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
  display: flex;
  flex-direction: column;
  height: 340px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s ease;
}

.chart-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.1);
}

.chart-card--wide {
  height: 360px;
}

.chart-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  flex-shrink: 0;
}

.chart-header-icon {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.chart-body {
  flex: 1;
  min-height: 0;
  padding: 8px 12px 12px;
}

.chart-body--wide {
  height: 100%;
}

.empty-chart {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

@media (max-width: 600px) {
  .pd-wrapper {
    padding: 1rem;
  }
}
</style>
